import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import HotelRewardPointsForm from "./HotelRewardPointsForm";
import * as XLSX from "xlsx";

const HotelRewardPoints = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [eventTypes, setEventTypes] = useState([]);
  const handleExportToExcel = () => {
    const formattedData = tableData.map((row) => ({
      ID: row.id,
      Hotel: row.hotel_name,
      "Event Type": row.event_type,
      "Reward Points": row.reward_points,
      "From Value": row.from_value,
      "To Value": row.to_value,
      "No of Points": row.no_of_points,
      Status: row.status,
      "Expiry Date": row.date_expired,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RewardPoints");
    XLSX.writeFile(workbook, "HotelRewardPoints.xlsx");
  };

  const columns = [
    { title: "ID", field: "id" },
    { title: "Hotel", field: "hotel_name" },
    { title: "Event Type", field: "event_type" },
    { title: "Reward Points", field: "reward_points" },
    { title: "From Value", field: "from_value" },
    { title: "To Value", field: "to_value" },
    { title: "No of Points", field: "no_of_points" },
    { title: "Status", field: "status" },
    { title: "Expiry Date", field: "date_expired" },
  ];

  const fetchData = async () => {
    try {
      const [pointsRes, hotelsRes] = await Promise.all([
        fetch("https://api.esthell.com/hm_reward_points"),
        fetch("https://api.esthell.com/Hotel"),
      ]);

      const pointsData = await pointsRes.json();
      const hotelsData = await hotelsRes.json();
      console.log("Fetched hotels:", hotelsData);
      if (hotelsData && Array.isArray(hotelsData.data))
        setHotels(hotelsData.data);
      else setHotels([]);
      if (pointsData && Array.isArray(pointsData.data)) {
        // Map hotel_id to hotel name for display
        const hotelMap =
          hotelsData && Array.isArray(hotelsData.data)
            ? Object.fromEntries(hotelsData.data.map((h) => [h.id, h.name]))
            : {};
        const tableRows = pointsData.data.map((row) => ({
          ...row,
          hotel_name: hotelMap[row.hotel_id] || row.hotel_id,
        }));
        setTableData(tableRows);
      } else {
        setTableData([]);
      }
    } catch (err) {
      setNotification({ open: true, message: err.message, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Fetch event types on mount
    const fetchEventTypes = async () => {
      try {
        const res = await fetch(
          "https://api.esthell.com/left-navigation/list",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ metadata_type: "event_type" }),
          },
        );
        const data = await res.json();
        if (data && Array.isArray(data.data)) {
          setEventTypes(data.data.map((e) => e.metadata_name));
        } else {
          setEventTypes([]);
        }
      } catch (err) {
        setEventTypes([]);
      }
    };
    fetchEventTypes();
  }, []);

  const handleFormSubmit = async (formData) => {
    try {
      const url = editData
        ? `https://api.esthell.com/hm_reward_points/${editData.id}`
        : `https://api.esthell.com/create_reward_points`;

      const method = editData ? "PUT" : "POST";

      // Format the payload according to the API requirements
      const payload = editData
        ? { data: formData } // For update, wrap in data object
        : formData; // For create, send as is

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!responseData.success) {
        throw new Error(responseData.message || "Operation failed");
      }

      setNotification({
        open: true,
        message: editData
          ? "Reward Points updated successfully!"
          : "Reward Points created!",
        type: "success",
      });

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setNotification({ open: true, message: err.message, type: "error" });
    }
  };

  const handleEdit = (row) => {
    // Make a copy of the row data to avoid direct mutation
    const rowData = { ...row };

    // Ensure hotel_id is included
    if (!rowData.hotel_id && rowData.hotel_name) {
      const hotel = hotels.find((h) => h.name === rowData.hotel_name);
      if (hotel) {
        rowData.hotel_id = hotel.id.toString();
      }
    }

    // Make sure status is a string
    if (rowData.status !== undefined) {
      rowData.status = rowData.status.toString();
    }

    // Format date properly
    if (rowData.date_expired) {
      try {
        const date = new Date(rowData.date_expired);
        if (!isNaN(date.getTime())) {
          rowData.date_expired = date.toISOString().split("T")[0];
        }
      } catch (e) {
        console.error("Error formatting date:", e);
      }
    }

    console.log("Editing row data:", rowData);
    setEditData(rowData);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    try {
      const res = await fetch(
        `https://api.esthell.com/hm_reward_points/${row.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      const responseData = await res.json();

      if (!responseData.success) {
        throw new Error(
          responseData.message || "Failed to delete reward point",
        );
      }

      setNotification({
        open: true,
        message: "Deleted successfully",
        type: "success",
      });
      fetchData();
    } catch (err) {
      setNotification({ open: true, message: err.message, type: "error" });
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Hotel Reward Points
        </Typography>
        <button
          onClick={handleExportToExcel}
          style={{
            padding: "8px 16px",
            background: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Export to Excel
        </button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <MaterialTable
          columns={columns}
          data={tableData}
          onEdit={handleEdit}
          onDelete={handleDelete}
          toolbar={{
            create: true,
            onCreateClick: () => setShowForm(true),
            extraButtons: [
              {
                label: "Export to Excel",
                onClick: handleExportToExcel,
                variant: "outlined",
                color: "primary",
              },
            ],
          }}
        />
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 3 }}>
          <HotelRewardPointsForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            onSubmit={handleFormSubmit}
            initialData={editData}
            hotels={hotels}
            eventTypes={eventTypes}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.type}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelRewardPoints;
